import { CourseEntity } from './course';
import { CustomerEntity } from './customer';
import { EntityBase } from './entity-base';
import { TrainingSchedule } from './requisition-model';

export class ClassesEntity extends EntityBase {
  requisitionId?: string;
  // Optional: API does not always return these fields
  customer?: CustomerEntity;
  trainingSchedule?: TrainingSchedule;
  course: CourseEntity | undefined;
  data: any;

  /**
   * Optional reference to the VM Connection Template assigned to this class. Populated by
   * the backend when serialising the class document. `null`/undefined means the launch path
   * falls back to the global `appsettings.json` Guacamole defaults.
   */
  vmConnectionTemplateId?: string | null;

  /**
   * Optional reference to the TenantSecret assigned to this class. Populated by the backend
   * as a top-level field (mirrors `ClassModel.TenantSecretId`). `null`/undefined means no
   * Azure credential has been assigned yet — provisioning for this class will fail until one is.
   */
  tenantSecretId?: string | null;

  get isCustom(): boolean {
    return this.course === undefined;
  }

  get description(): string {
    return this.isCustom ? this.name : (this.course?.description ?? '');
  }

  get participants(): string {
    return this.trainingSchedule?.participants?.join(', ') ?? '';
  }

  get trainers(): string {
    return this.trainingSchedule?.trainers?.map(t => t.name).join(', ') ?? '';
  }

  get labs(): string {
    return this.course?.labs?.map(lab => lab.name).join(', ') ?? '';
  }

  /** Returns undefined when trainingSchedule is not yet loaded. */
  get status(): 'OnGoing' | 'Upcoming' | 'Completed' | undefined {
    if (!this.trainingSchedule) return undefined;
    const today = new Date();
    if (this.trainingSchedule.startDate > today) {
      return 'Upcoming';
    } else if (this.trainingSchedule.endDate < today) {
      return 'Completed';
    } else {
      return 'OnGoing';
    }
  }

  get startDate(): Date | undefined {
    return this.trainingSchedule?.startDate;
  }

  get startTime(): string | undefined {
    return this.trainingSchedule?.startTime;
  }

  get endDate(): Date | undefined {
    return this.trainingSchedule?.endDate;
  }

  get endTime(): string | undefined {
    return this.trainingSchedule?.endTime;
  }

  constructor(trainingSchedule?: TrainingSchedule) {
    super();
    if (trainingSchedule) {
      this.trainingSchedule = trainingSchedule;
      this.requisitionId = trainingSchedule.reqId;
      this.course = trainingSchedule.course;
      this.name = this.isCustom
        ? `${this.trainingSchedule.title} (Custom Course)`
        : (this.course?.name ?? this.trainingSchedule.title ?? '');
    }
  }
}
